import { Button } from "@/components/kit";
import SvgIcon from "@/components/common/SvgIcon";
import Filter from "@/components/kit/Filters/Filters";
import { useQueryParams } from "@/hooks/general/useQueryParams";
import { ChangeEvent, useEffect, useMemo } from "react";
import TextboxWithFilter from "@/components/common/TextboxWithFilter";
import { useNavigate } from "react-router-dom";
import { debounce } from "@/utilize/debounce";

import { TOrganizationParamsType } from "../../types";
import useGerOrganizations from "@/hooks/query/organization/useGeTOrganizations";
import OrganizationList from "./OrganizationsList";
import useBreadcrumbs from "@/hooks/store/useBreadcrumbs";
import NoResult from "./NoResult";
import EmptyStateWithIcon from "@/components/common/EmptyStateWithIcon";
import PermissionGate from "@/components/PermissionGate";
import { PERMISSIONS } from "@/constants/permissions";

const sortOptions = [{ label: "Name", value: "name" }];

const Organizations = () => {
  const { params } = useQueryParams<TOrganizationParamsType>({
    page: 1,
    limit: 25,
  });

  const { data, isFetching } = useGerOrganizations(params);
  const { setRoutes } = useBreadcrumbs();
  const navigate = useNavigate();

  const hasFilter = useMemo(() => {
    const { page, limit, orderBy, orderDirection, ...restParams } = params;

    return Object.values(restParams).some((value) => value !== undefined);
  }, [params, data]);

  const renderContent = useMemo(() => {
    const deviceGroups = data?.data.items;
    const isEmpty = !deviceGroups || deviceGroups.length === 0;
    const renderWithFilter = (children: React.ReactNode) => (
      <WithFilters>{children}</WithFilters>
    );
    if (hasFilter && isEmpty && !isFetching) {
      return renderWithFilter(<NoResult />);
    }

    if (!hasFilter && isEmpty && !isFetching) {
      return (
        <div className="flex justify-center items-center h-full">
          <EmptyStateWithIcon
            title="No organizations yet"
            description="Add organizations to start managing clients, devices,
and activities from a unified dashboard."
            button={
              <Button
                variant="gradient"
                className="!flex items-center !gap-2 w-[116px] h-[40px]"
                onClick={() => navigate("/organizations/add")}
              >
                <SvgIcon
                  className="w-5 h-5"
                  iconName="plus"
                  fill="fill-white"
                />
                <span>Add</span>
              </Button>
            }
          />
        </div>
      );
    }

    return (
      <WithFilters>
        <OrganizationList
          data={data?.data.items || []}
          isFetching={isFetching}
          meta={data?.data.meta}
        />
      </WithFilters>
    );
  }, [hasFilter, data, isFetching]);
  useEffect(() => {
    setRoutes([
      {
        name: "Organizations",
        path: "/organizations",
        description:
          "Each organization represents clients or businesses you manage. Configure policies, devices, and users independently for better control.",
        helpLink:
          "https://help.trio.so/en/articles/12521985-managing-organizations",
      },
    ]);
    return () => {
      setRoutes([]);
    };
  }, [setRoutes]);

  return <div className=" pt-[18px] h-full">{renderContent}</div>;
};

export default Organizations;

const WithFilters = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const { setParams, params, deleteParam } =
    useQueryParams<TOrganizationParamsType>({
      page: 1,
      limit: 25,
      orderDirection: "DESC",
      orderBy: "name",
    });
  const debounced = debounce((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setParams({ ...params, page: 1, search: e.target.value });
    } else {
      deleteParam("search");
    }
  });

  return (
    <div className="h-full flex flex-col">
      <div className="px-8  flex flex-col ">
        <div className="flex items-center gap-2">
          <TextboxWithFilter
            value={params.search}
            placeholder="Search in name"
            onChange={debounced}
          />

          <PermissionGate permission={PERMISSIONS.ORGANIZATION_CREATE}>
            <Button
              variant="gradient"
              className="!flex items-center !gap-2 w-[116px] h-[40px]"
              onClick={() => navigate("/organizations/add")}
            >
              <SvgIcon className="w-5 h-5" iconName="plus" fill="fill-white" />
              <span>Add</span>
            </Button>
          </PermissionGate>
        </div>
        <div className="mt-2 mb-4">
          <Filter<typeof params>
            onFiltersChange={(newFilters) => {
              setParams({ ...params, ...newFilters, page: 1 });
            }}
            values={params}
            onClearFilter={() => {
              deleteParam("search");
              deleteParam("orderBy");

              deleteParam("page");
              deleteParam("limit");
              deleteParam("orderDirection");
            }}
          >
            <Filter.FilterSort
              label="SortBy"
              keyName={"orderBy"}
              options={sortOptions}
              dataToShow={
                sortOptions.find((option) => option.value === params.orderBy)
                  ?.label
              }
            />
          </Filter>
        </div>
      </div>

      {children}
    </div>
  );
};
